import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useState,
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import type { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Dropdown, type DropdownChangeEvent } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { Controller, type FieldPath, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  AccountAutoComplete,
  BreedsAutoComplete,
  defaultGradingRows,
  GradingGrid,
  type GradingGridRow,
  gridToStageGradeCount,
  SitesAutoComplete,
  StringMapDropdown,
  YardsAutoComplete,
} from '@/components/common';
import { AdvancedDonorLookup } from '@/components/common/advanced-donor-lookup';
import { AdvancedSireLookup } from '@/components/common/advanced-sire-lookup';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import {
  FRESH_FROZEN_OPTIONS,
  LOAD_DONOR_PARTNERS_OPTIONS,
  TRANSFER_CLINIC_RANCH_HAULIN_OPTIONS,
} from '@/components/common/create-outside-embryos-wizard/constants';
import { sumGradingRows } from '@/components/common/grading-grid/utils';
import { StringMapObjectTypeCode } from '@/components/common/string-map-dropdown';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import {
  EMBRYO_METHOD_OPTIONS,
  FRESH_FROZEN,
  SEX_EMBRYO,
  TRANSFER_CLINIC_RANCH_HAULIN,
} from '@/constants';
import type { Donor, Sire } from '@/rtk-query.gql';
import {
  OnFarmImplantRequestError,
  type OnFarmImplantSession,
  useCreateOnFarmImplantNonTogEmbryos,
} from '@/services/on-farm-implant';
import { parseWallClock, toWallClockISO } from '@/utils/wallClock';

import {
  NON_TOG_GENERAL_FIELDS,
  nonTogEmbryoFormSchema,
  type NonTogEmbryoFormValues,
} from './schema';

type DefaultValue<P extends { defaultValue?: unknown }> = P['defaultValue'];

const sexOptions = Object.entries(SEX_EMBRYO).map(([label, value]) => ({
  label,
  value,
}));

const newGradingRows = () => defaultGradingRows.map((row) => ({ ...row }));

const buildDefaults = (
  session: OnFarmImplantSession,
): Partial<NonTogEmbryoFormValues> => {
  const account = session.accountId
    ? { id: session.accountId, name: session.accountName, code: null }
    : undefined;
  return {
    donor: undefined,
    sire: undefined,
    freezeOwner: account,
    breed: undefined,
    method: undefined,
    recoveryFirmCode: '',
    recoveryFirmName: '',
    freshFrozen: FRESH_FROZEN.Fresh,
    sex: undefined,
    implantDate: parseWallClock(session.sessionDate) ?? undefined,
    transferQuantity: 0,
    implantTierOf: undefined,
    loadDonorPartners: undefined,
    transferSite: { id: session.siteId, name: session.siteName ?? '' },
    transferClinicRanchHaulIn: TRANSFER_CLINIC_RANCH_HAULIN.RANCH,
    transferTechName: '',
    implantType: undefined,
    recipientOwner: undefined,
    deyard: undefined,
    transferOwner: account,
  };
};

type AddNonTogEmbryosDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: OnFarmImplantSession;
};

export const AddNonTogEmbryosDialog = ({
  open,
  onOpenChange,
  session,
}: AddNonTogEmbryosDialogProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [problems, setProblems] = useState<string[]>([]);
  const [gradingRows, setGradingRows] =
    useState<GradingGridRow[]>(newGradingRows);

  const { mutateAsync: createEmbryos, isPending } =
    useCreateOnFarmImplantNonTogEmbryos();

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    setValue,
    formState: { errors },
  } = useForm<NonTogEmbryoFormValues>({
    resolver: zodResolver(nonTogEmbryoFormSchema),
    defaultValues: buildDefaults(session),
  });

  const resetAll = useCallback(() => {
    reset(buildDefaults(session));
    setGradingRows(newGradingRows());
    setProblems([]);
    setStep(0);
  }, [reset, session]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) resetAll();
      onOpenChange(nextOpen);
    },
    [onOpenChange, resetAll],
  );

  const handleNext = useCallback(async () => {
    if (await trigger([...NON_TOG_GENERAL_FIELDS])) setStep(1);
  }, [trigger]);

  const onSubmit = useCallback(
    async (data: NonTogEmbryoFormValues) => {
      setProblems([]);
      try {
        await createEmbryos({
          sessionId: session.sessionId,
          input: {
            donorId: data.donor.id,
            sireId: data.sire.id,
            freezeOwnerId: data.freezeOwner.id,
            transferOwnerId: data.transferOwner?.id ?? null,
            breedId: data.breed?.id ?? null,
            method: data.method ?? null,
            recoveryFirmCode: data.recoveryFirmCode || null,
            recoveryFirmName: data.recoveryFirmName || null,
            freshFrozen: data.freshFrozen,
            sex: data.sex ?? null,
            implantDate: toWallClockISO(data.implantDate),
            implantTierOf: data.implantTierOf ?? null,
            loadDonorPartners: data.loadDonorPartners ?? null,
            transferSiteId: data.transferSite.id,
            transferClinicRanchHaulIn: data.transferClinicRanchHaulIn,
            transferTechName: data.transferTechName || null,
            implantType: data.implantType ?? null,
            recipientOwnerId: data.recipientOwner?.id ?? null,
            deyardId: data.deyard?.id ?? null,
            implantedEmbryoStageGradeCount: gridToStageGradeCount(gradingRows),
          },
        });
        toast.add({
          data: { variant: 'success' },
          title: t('onFarmImplant.nonTog.addSuccess', {
            count: data.transferQuantity,
          }),
        });
        handleOpenChange(false);
      } catch (error) {
        if (
          error instanceof OnFarmImplantRequestError &&
          error.problems.length
        ) {
          setProblems(error.problems);
          return;
        }
        toast.add({
          data: { variant: 'error' },
          title: t('onFarmImplant.nonTog.addFailed'),
        });
      }
    },
    [createEmbryos, gradingRows, handleOpenChange, session.sessionId, t],
  );

  const errorFor = (name: FieldPath<NonTogEmbryoFormValues>) => {
    const message = errors[name as keyof NonTogEmbryoFormValues]?.message;
    return message ? (
      <small className="text-sm text-red-500">{t(message)}</small>
    ) : null;
  };

  const field = (
    label: string,
    name: FieldPath<NonTogEmbryoFormValues>,
    input: ReactNode,
    required = false,
  ) => (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      {input}
      {errorFor(name)}
    </div>
  );

  const generalStep = (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {field(
        t('onFarmImplant.nonTog.donor'),
        'donor',
        <Controller
          name="donor"
          control={control}
          render={({ field: f, fieldState }) => (
            <AdvancedDonorLookup
              value={f.value as Donor}
              className={classNames('w-full text-sm', {
                'p-invalid': fieldState.invalid,
              })}
              onChange={(donor) => f.onChange(donor)}
            />
          )}
        />,
        true,
      )}
      {field(
        t('onFarmImplant.nonTog.sire'),
        'sire',
        <Controller
          name="sire"
          control={control}
          render={({ field: f, fieldState }) => (
            <AdvancedSireLookup
              value={f.value as Sire}
              className={classNames('w-full text-sm', {
                'p-invalid': fieldState.invalid,
              })}
              onChange={(sire) => f.onChange(sire)}
              showEditButton={false}
              showAddButton={false}
            />
          )}
        />,
        true,
      )}
      {field(
        t('onFarmImplant.nonTog.owner'),
        'freezeOwner',
        <Controller
          name="freezeOwner"
          control={control}
          render={({ field: f, fieldState }) => (
            <AccountAutoComplete
              id={f.name}
              defaultValue={
                f.value as unknown as DefaultValue<
                  ComponentProps<typeof AccountAutoComplete>
                >
              }
              defaultId={f.value?.id}
              className={classNames('w-full text-sm', {
                'p-invalid': fieldState.invalid,
              })}
              onSelect={(e: AutoCompleteSelectEvent) => f.onChange(e.value)}
              appendTo={undefined}
            />
          )}
        />,
        true,
      )}
      {field(
        t('onFarmImplant.nonTog.breed'),
        'breed',
        <Controller
          name="breed"
          control={control}
          render={({ field: f }) => (
            <BreedsAutoComplete
              defaultValue={
                (f.value ?? undefined) as unknown as DefaultValue<
                  ComponentProps<typeof BreedsAutoComplete>
                >
              }
              onSelect={(e: AutoCompleteSelectEvent) => f.onChange(e.value)}
              className="w-full text-sm"
              appendTo={undefined}
            />
          )}
        />,
      )}
      {field(
        t('onFarmImplant.nonTog.method'),
        'method',
        <Controller
          name="method"
          control={control}
          render={({ field: f }) => (
            <Dropdown
              id={f.name}
              value={f.value}
              options={EMBRYO_METHOD_OPTIONS}
              className="w-full text-sm"
              onChange={(e) => f.onChange(e.value)}
              placeholder={t('onFarmImplant.nonTog.select')}
              showClear
            />
          )}
        />,
      )}
      {field(
        t('onFarmImplant.nonTog.sex'),
        'sex',
        <Controller
          name="sex"
          control={control}
          render={({ field: f }) => (
            <Dropdown
              id={f.name}
              value={f.value}
              options={sexOptions}
              className="w-full text-sm"
              onChange={(e) => f.onChange(e.value)}
              placeholder={t('onFarmImplant.nonTog.select')}
              showClear
            />
          )}
        />,
      )}
      {field(
        t('onFarmImplant.nonTog.recoveryFirmCode'),
        'recoveryFirmCode',
        <Controller
          name="recoveryFirmCode"
          control={control}
          render={({ field: f }) => (
            <InputText
              id={f.name}
              value={f.value ?? ''}
              className="w-full text-sm"
              onChange={(e) => f.onChange(e.target.value)}
            />
          )}
        />,
      )}
      {field(
        t('onFarmImplant.nonTog.recoveryFirmName'),
        'recoveryFirmName',
        <Controller
          name="recoveryFirmName"
          control={control}
          render={({ field: f }) => (
            <InputText
              id={f.name}
              value={f.value ?? ''}
              className="w-full text-sm"
              onChange={(e) => f.onChange(e.target.value)}
            />
          )}
        />,
      )}
      {field(
        t('onFarmImplant.nonTog.freshFrozen'),
        'freshFrozen',
        <Controller
          name="freshFrozen"
          control={control}
          render={({ field: f, fieldState }) => (
            <Dropdown
              id={f.name}
              value={f.value}
              options={FRESH_FROZEN_OPTIONS}
              className={classNames('w-full text-sm', {
                'p-invalid': fieldState.invalid,
              })}
              onChange={(e) => f.onChange(e.value)}
              placeholder={t('onFarmImplant.nonTog.select')}
            />
          )}
        />,
        true,
      )}
    </div>
  );

  const transferStep = (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {field(
          t('onFarmImplant.nonTog.implantDate'),
          'implantDate',
          <Controller
            name="implantDate"
            control={control}
            render={({ field: f, fieldState }) => (
              <Calendar
                id={f.name}
                value={f.value}
                className={classNames('w-full', {
                  'p-invalid': fieldState.invalid,
                })}
                onChange={(e) => f.onChange(e.value)}
                showIcon
                dateFormat="mm/dd/yy"
                showOnFocus={false}
                placeholder="mm/dd/yyyy"
              />
            )}
          />,
          true,
        )}
        {field(
          t('onFarmImplant.nonTog.transferSite'),
          'transferSite',
          <Controller
            name="transferSite"
            control={control}
            render={({ field: f, fieldState }) => (
              <SitesAutoComplete
                defaultValue={
                  f.value as unknown as DefaultValue<
                    ComponentProps<typeof SitesAutoComplete>
                  >
                }
                onSelect={(e: AutoCompleteSelectEvent) => f.onChange(e.value)}
                className={classNames('w-full text-sm', {
                  'p-invalid': fieldState.invalid,
                })}
                appendTo={undefined}
              />
            )}
          />,
          true,
        )}
        {field(
          t('onFarmImplant.nonTog.clinicRanchHaulIn'),
          'transferClinicRanchHaulIn',
          <Controller
            name="transferClinicRanchHaulIn"
            control={control}
            render={({ field: f, fieldState }) => (
              <Dropdown
                id={f.name}
                value={f.value}
                options={TRANSFER_CLINIC_RANCH_HAULIN_OPTIONS}
                className={classNames('w-full text-sm', {
                  'p-invalid': fieldState.invalid,
                })}
                onChange={(e) => f.onChange(e.value)}
                placeholder={t('onFarmImplant.nonTog.select')}
              />
            )}
          />,
          true,
        )}
        {field(
          t('onFarmImplant.nonTog.transferTech'),
          'transferTechName',
          <Controller
            name="transferTechName"
            control={control}
            render={({ field: f }) => (
              <InputText
                id={f.name}
                value={f.value ?? ''}
                className="w-full text-sm"
                onChange={(e) => f.onChange(e.target.value)}
              />
            )}
          />,
        )}
        {field(
          t('onFarmImplant.nonTog.implantType'),
          'implantType',
          <Controller
            name="implantType"
            control={control}
            render={({ field: f }) => (
              <StringMapDropdown
                value={f.value ?? undefined}
                objectTypeCode={StringMapObjectTypeCode.IMPLANT_TYPE}
                attributeName="tog_implantType"
                className="w-full text-sm"
                onChange={(e: DropdownChangeEvent) => f.onChange(e.value)}
              />
            )}
          />,
        )}
        {field(
          t('onFarmImplant.nonTog.recipientOwner'),
          'recipientOwner',
          <Controller
            name="recipientOwner"
            control={control}
            render={({ field: f }) => (
              <AccountAutoComplete
                id={f.name}
                defaultValue={
                  (f.value ?? undefined) as unknown as DefaultValue<
                    ComponentProps<typeof AccountAutoComplete>
                  >
                }
                defaultId={f.value?.id}
                className="w-full text-sm"
                onSelect={(e: AutoCompleteSelectEvent) => f.onChange(e.value)}
                appendTo={undefined}
              />
            )}
          />,
        )}
        {field(
          t('onFarmImplant.nonTog.yard'),
          'deyard',
          <Controller
            name="deyard"
            control={control}
            render={({ field: f }) => (
              <YardsAutoComplete
                defaultValue={
                  (f.value ?? undefined) as unknown as DefaultValue<
                    ComponentProps<typeof YardsAutoComplete>
                  >
                }
                onSelect={(e: AutoCompleteSelectEvent) => f.onChange(e.value)}
                className="w-full text-sm"
                appendTo={undefined}
              />
            )}
          />,
        )}
        {field(
          t('onFarmImplant.nonTog.transferOwner'),
          'transferOwner',
          <Controller
            name="transferOwner"
            control={control}
            render={({ field: f }) => (
              <AccountAutoComplete
                id={f.name}
                defaultValue={
                  (f.value ?? undefined) as unknown as DefaultValue<
                    ComponentProps<typeof AccountAutoComplete>
                  >
                }
                defaultId={f.value?.id}
                className="w-full text-sm"
                onSelect={(e: AutoCompleteSelectEvent) => f.onChange(e.value)}
                appendTo={undefined}
              />
            )}
          />,
        )}
        {field(
          t('onFarmImplant.nonTog.implantTierOf'),
          'implantTierOf',
          <Controller
            name="implantTierOf"
            control={control}
            render={({ field: f }) => (
              <InputNumber
                inputId={f.name}
                locale="en-US"
                value={f.value ?? null}
                onValueChange={(e) => f.onChange(e.value ?? null)}
                className="w-full"
                inputClassName="w-full"
              />
            )}
          />,
        )}
        {field(
          t('onFarmImplant.nonTog.loadDonorPartners'),
          'loadDonorPartners',
          <Controller
            name="loadDonorPartners"
            control={control}
            render={({ field: f }) => (
              <Dropdown
                id={f.name}
                value={f.value}
                options={LOAD_DONOR_PARTNERS_OPTIONS}
                className="w-full text-sm"
                onChange={(e) => f.onChange(e.value)}
                placeholder={t('onFarmImplant.nonTog.select')}
                showClear
              />
            )}
          />,
        )}
      </div>
      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-semibold text-gray-700">
          {t('onFarmImplant.nonTog.gradingTitle')}
        </h4>
        <GradingGrid
          rowData={gradingRows}
          storageKey="on-farm-implant-non-tog-grading-grid"
          onCellValueChanged={(updated) => {
            setGradingRows(updated);
            setValue('transferQuantity', sumGradingRows(updated), {
              shouldValidate: true,
            });
          }}
        />
        {errorFor('transferQuantity')}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="md:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{t('onFarmImplant.nonTog.addTitle')}</DialogTitle>
          <DialogDescription>
            {step === 0
              ? t('onFarmImplant.nonTog.stepGeneral')
              : t('onFarmImplant.nonTog.stepTransfer')}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto">
          {problems.length > 0 && (
            <ul className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          )}
          {step === 0 ? generalStep : transferStep}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          {step === 1 && (
            <Button variant="secondary" onClick={() => setStep(0)}>
              {t('onFarmImplant.nonTog.back')}
            </Button>
          )}
          {step === 0 ? (
            <Button onClick={() => void handleNext()}>
              {t('onFarmImplant.nonTog.next')}
            </Button>
          ) : (
            <Button
              disabled={isPending}
              onClick={() => void handleSubmit(onSubmit)()}
            >
              {isPending && <i className="pi pi-spin pi-spinner" />}
              {t('onFarmImplant.nonTog.save')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
